import React, { ChangeEvent, Dispatch, ReactElement, SetStateAction, useEffect, useState } from 'react';
import { TFunction, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { UserService } from "../services/UserService";
import { UtilService } from "../services/UtilService";
import { WebCache } from "../services/WebCache";
import LabelMessage from './LabelMessage';
import "./Login.scss";
import "./Onboarding.scss";
import Form from './containers/form';
import SubmitButton from './controls/buttons/submitButton';
import EmailField from './controls/fields/emailField';
import AuthenticatorCodeField from './controls/fields/codeInput'
import PasswordField from './controls/fields/passwordField';
import { ReactComponent as CodeAuthenticatorSVG } from "../../node_modules/itd-common/assets/images/onboarding/illus_2fa.svg";
import Button from './controls/buttons/button';
import TextField from './controls/fields/textField';
import Card from './containers/card';
import Footer from './layout/Footer';
import { IAuthenticateRequest, IOtpRequest } from '../requests/ILoginRequest';
import SelectField from './controls/fields/selectField';
import { TOTPService } from '../services/TOTPService';
import { OptionMapperHelper } from '../helpers/OptionMapperHelper';
import { QRCodeSVG } from 'qrcode.react';
import { ActivateDeviceRequest, DeviceRequest, UnpairOtpDevicesRequest } from '../requests/ITOTPRequest';
import { IBaseDevice, ICreateDevice } from '../models/ITOTPDevice';
import { ObjectHelper } from '../helpers/ObjectHelper';
import { StringHelper } from '../helpers/StringHelper';
import { Option } from '../models/Option';
import { toast, ToastContainer } from 'react-toastify';
import { ReactComponent as SendMail } from "../../node_modules/itd-common/assets/images/onboarding/illus_email.svg";
import { AuthAsideDefault, AuthAsideOtp, AuthDownload2FA } from '../components/layout/auth';
import Icon from './controls/icon/icon';
import BrandLogo from '../components/layout/auth/authLogoModal';
import StepProgress, { PageActivateSteps, PageModeOnboarding } from './layout/auth/onboardingSteps';
import QrScan from './layout/auth/authQrScan';
import Code2FA from './layout/auth/code2FA';
import { ReactComponent as ExpiredPasswordSVG } from "../../node_modules/itd-common/assets/images/onboarding/expired_password_01.svg";


enum PageMode {
    LOGIN,
    PHONE_NUMBER_REQUEST,
    FINGERPRINT_REGISTRATION,
    OTP_CODE_REQUEST,
    OTP_CODE_REQUEST_LOST_DEVICE,
    OTP_CREATE_DEVICE,
    OTP_ACTIVATE_DEVICE,
    OTP_CHOICE_DEVICE
}

type RenderObject = {
    title: string,
    renderFunction: () => ReactElement,
    renderAsideFunction?: () => ReactElement,
    initialize?: {
        state: [any, Dispatch<SetStateAction<any>>],
        function: <T>(state: [T, Dispatch<SetStateAction<T>>]) => void
    }
};

type LoginFormType = { username: string, key: string };
type CodeFormType = { code: string };
type PhoneFormType = { number: string };
type ChoiceDeviceFormType = { device: string };
type NameType = { name: string };
type RegistrationUriType = { registrationUri: string };
type ActivateDeviceFormType = CodeFormType & RegistrationUriType;
type DeviceType = NameType & { authId?: string, token?: string }

interface IProps {
    t: TFunction
}

let loginLoaded = false;
const T_PREFIX: string = 'pages.login.';

const Login: React.FC<IProps> = ({ t }: IProps) => {
    const [pageMode, setPageMode] = useState<PageMode>(PageMode.LOGIN);
    const [pageActivateStep, setPageActiveStep] = useState<PageActivateSteps>(PageActivateSteps.CONFIG);
    const [loginForm, setLoginForm] = useState<LoginFormType>({ username: '', key: '' });
    const [phoneForm, setPhoneForm] = useState<PhoneFormType>({ number: '' });
    const [otpCodeForm, setOtpCodeForm] = useState<CodeFormType>({ code: '' });
    const [otpActivateDeviceForm, setOtpActivateDeviceForm] = useState<ActivateDeviceFormType>({ code: '', registrationUri: '' });
    const [otpChoiceDeviceForm, setOtpChoiceDeviceForm] = useState<ChoiceDeviceFormType>({ device: '' });
    const [otpChoiceDeviceOptions, setOtpChoiceDeviceOptions] = useState<Option[]>([]);
    const [device, setDevice] = useState<DeviceType>({ name: '', authId: '', token: '' });
    const [serverError, setServerError] = useState<string>("");
    const [isValid, setIsValid] = useState<boolean>(false);
    const [isLoginValid, setIsLoginValid] = useState<boolean>(false);
    const [isPhoneNumberValid, setIsPhoneNumberValid] = useState<boolean>(true);
    const [isOTPCodeValid, setIsOTPCodeValid] = useState<boolean>(true);
    const [isSubmitAllowed, setIsSubmitAllowed] = useState<boolean>(true);
    const [fingerPrintEnabled] = useState<boolean>(localStorage.getItem('credential') !== null);
    const [initLogin, setInitLogin] = useState<boolean>(false);
    const [bgImage, setBgImage] = useState("/bgmerchantcenter.jpg");
    const [copiedQR, setCopiedQR] = useState(false);
    const [showExpiredPassword, setShowExpiredPassword] = useState<boolean>(false);


    useEffect(() => {
        const updateBg = () => {
            setBgImage(window.innerWidth <= 425 ? "/bgmerchantcentermobile.jpg" : "/bgmerchantcenter.jpg");
        };
        updateBg();
        window.addEventListener("resize", updateBg);
        return () => window.removeEventListener("resize", updateBg);
    }, []);

    useEffect(() => {
        setInitLogin(false);
        if (!initLogin) {
            if(window.location.href.indexOf("merchantToken=") > 0){
                let merchantToken = window.location.href.split("merchantToken=")[1];
                if(merchantToken && merchantToken.length > 0) {
                    UserService.loginWithToken(merchantToken).then(() => {
                        WebCache.initCache().then(() => {
                            if (UtilService.mobileCheck()) {
                                setPageMode(PageMode.FINGERPRINT_REGISTRATION);
                            }
                            else {
                                navigateToDashboard();
                            }
                        }).catch(() => setIsValid(false));
                    }).catch(() => setIsValid(false))
                }
            }
        }
    }, [initLogin]);

    const registerFingerPrint = (): void => {
        UserService.getWebAuthnRegistrationOptions().then(function (res) {
            const credentialCreationOptions = res.data;
            credentialCreationOptions.challenge = StringHelper.base64DecodeURL(credentialCreationOptions.challenge);
            credentialCreationOptions.user.id = StringHelper.base64DecodeURL(credentialCreationOptions.user.id);
            credentialCreationOptions.user.name = loginForm.username;
            credentialCreationOptions.user.displayName = 'Visor transacciones ITD';
            if (credentialCreationOptions.excludeCredentials != null) {
                for (var i in credentialCreationOptions.excludeCredentials) {
                    credentialCreationOptions.excludeCredentials[i].id = StringHelper.base64DecodeURL(credentialCreationOptions.excludeCredentials[i].id);
                }
            }
            navigator.credentials.create({
                publicKey: credentialCreationOptions
            }).then(function (credential: any) {

                const credentialId = StringHelper.base64Encode(credential.rawId);

                localStorage.setItem('credential', JSON.stringify({ credentialId }));
                localStorage.setItem('lastUserName', loginForm.username);

                //Registro la huella
                const data = {
                    AttestationResponse: {
                        rawId: credentialId,
                        id: credential.id,
                        type: credential.type,
                        response: {
                            attestationObject: StringHelper.base64Encode(credential.response.attestationObject),
                            clientDataJSON: StringHelper.base64Encode(credential.response.clientDataJSON)
                        }
                    },
                    AdditionalData: StringHelper.base64Encode(credential.response.clientDataJSON)
                };
                UserService.webAuthnRegistration(data);
                navigateToDashboard();
            });
        }, function (error: any) {
        });
    }

    const navigateToDashboard = (): void => {
        window.location.href = '/dashboard'
    }

    const handleCancel = () => {
        const renderObject: RenderObject = getRenderObjectByPageMode(pageMode);

        if (renderObject.initialize) {
            renderObject.initialize.function(renderObject.initialize.state);
        }

        setPageMode(PageMode.LOGIN);
    }

    const loginWithFingerPrint = (): void => {
        UserService.assertionOptions().then(function (res: any) {
            let credentialRequestOptions = res.data;
            let credentialId = JSON.parse(localStorage.getItem('credential') + "");
            credentialRequestOptions.challenge = StringHelper.base64DecodeURL(credentialRequestOptions.challenge);
            credentialRequestOptions.allowCredentials = [
                {
                    id: StringHelper.base64ToBuffer(credentialId.credentialId),
                    type: 'public-key',
                    transports: ['internal']
                }
            ];
            navigator.credentials.get({
                publicKey: credentialRequestOptions
            }).then(function (credential: any) {
                const data = {
                    rawId: StringHelper.base64Encode(credential.rawId),
                    id: credential.id,
                    type: credential.type,
                    userHandle: StringHelper.base64Encode(credential.response.userHandle),
                    response: {
                        authenticatorData: StringHelper.base64Encode(credential.response.authenticatorData),
                        signature: StringHelper.base64Encode(credential.response.signature),

                        clientDataJSON: StringHelper.base64Encode(credential.response.clientDataJSON),
                    }
                };
                UserService.makeAssertion(data).then(function (res) {
                    if (!res.data.success) {
                        localStorage.clear();
                    }
                    WebCache.initCache().then(() => {
                        navigateToDashboard();
                    });
                });
            }, function (error: any) {
                console.error(error);
            });
        })
    }

    const requestOtp = (): void => {
        const requestOtp: IOtpRequest = {
            username: loginForm.username,
            password: loginForm.key,
            deviceName: device.name,
            phoneNumber: phoneForm.number,
            typeAuthentication: "TOTP"
        };

        UserService.requestOtp(requestOtp)
            .then((response) => {
                if (response.data?.message === "needToResetPassword") {
                    setShowExpiredPassword(true);
                    return null;
                }
                return response.data.data;
            })
            .then((data) => {
                if (!data) return;
                if(!data.success){
                    setIsValid(false);
                    setIsLoginValid(true)
                    return;
                }
                const devices: Array<string> = data.totP_Devices;

                if (!devices) {
                    if (data.totpAuthId && data.totpToken) {
                        setDevice({ name: data.deviceName, authId: data.totpAuthId, token: data.totpToken });
                        setPageMode(PageMode.OTP_CODE_REQUEST);
                    }

                    else {
                        const request: DeviceRequest = { user: loginForm.username, deviceName: loginForm.username };
                        TOTPService.createDevice(request)
                            .then((response) => response.data)
                            .then((device: ICreateDevice) => {
                                setOtpActivateDeviceForm({ registrationUri: device.registrationUri || '', code: '' });
                                setDevice({ name: device.deviceName || '', authId: device.authId, token: device.token });
                                setPageMode(PageMode.OTP_ACTIVATE_DEVICE);
                                setIsValid(true);
                            }).catch((error) => {
                                console.error(error);
                                setIsValid(false);
                            });
                    }
                    return;
                }
                if (devices && devices.length > 1) {
                    const deviceOptions: Array<HTMLOptionElement> = new OptionMapperHelper(t).buildSelectOptionsFromEnum(devices) as Array<HTMLOptionElement>;
                    setOtpChoiceDeviceOptions(deviceOptions);
                    setOtpChoiceDeviceForm({ device: deviceOptions[0].value });
                    setPageMode(PageMode.OTP_CHOICE_DEVICE);
                    return;
                }
                if (!data.phoneNumberValidated) {
                    setPageMode(PageMode.PHONE_NUMBER_REQUEST);
                    return;
                }
                setIsValid(true);
                setIsLoginValid(false)
                setIsOTPCodeValid(true);
                setPageMode(PageMode.OTP_CODE_REQUEST);
            })
            .catch((error) => {
                setServerError(error?.response?.data?.message || "");
                setIsValid(false);
                setIsLoginValid(true);
            });
    };

    const validatePhoneNumberAndRequestOtp = (): void => {
        let phoneNumberValid: boolean = false;
        if (phoneForm.number) {
            const validatePhoneNumber = (phoneNumber: string): boolean => {
                return /^((\+|00)?\d{1,3}\s?)?(\(\d{1,4}\)|\d{1,4})?[-\s]?\d{3,4}[-\s]?\d{3,4}$/.test(phoneNumber);
            }
            phoneNumberValid = validatePhoneNumber(phoneForm.number);
            setIsPhoneNumberValid(phoneNumberValid);
        }

        if (phoneNumberValid) {
            setPageMode(PageMode.LOGIN);
            requestOtp();
        }
        else {
            setIsPhoneNumberValid(false);
        }
    };

    const login = (): void => {
        const authenticateRequest: IAuthenticateRequest = {
            username: loginForm.username,
            password: loginForm.key,
            otp: otpCodeForm.code,
            deviceName: device.name,
            totpToken: device.token,
            totpAuthId: device.authId
        };

        setIsSubmitAllowed(false);
        setIsOTPCodeValid(true);
        UserService.login(authenticateRequest)
            .then((response) => {
                if (response.data && response.data.success === false) {
                    setIsOTPCodeValid(false);
                    setIsSubmitAllowed(true);
                    if (response.data.message) {
                        setServerError(response.data.message);
                    }
                    return;
                }
                WebCache.initCache().then(() => {
                    const lastSubmitted = localStorage.getItem('nps_last_submitted');
                    const lastShown = localStorage.getItem('nps_last_shown');
                    const daysSinceSubmitted = lastSubmitted ? (Date.now() - Number(lastSubmitted)) / (1000 * 60 * 60 * 24) : Infinity;
                    const daysSinceShown = lastShown ? (Date.now() - Number(lastShown)) / (1000 * 60 * 60 * 24) : Infinity;
                    if (daysSinceSubmitted >= 90 && daysSinceShown >= 7) {
                        localStorage.setItem('nps_pending_login', 'true');
                    }
                    // To enable the fingerprint functionality, uncomment this if code
                    // if (UtilService.mobileCheck()) {
                    //     setPageMode(PageMode.FINGERPRINT_REGISTRATION);
                    // }
                    // else {
                    //     navigateToDashboard();
                    // }
                    // To enable the fingerprint functionality, remove this navigateToDashboard() call.
                    navigateToDashboard();
                }).catch(() => setIsValid(false));
            })
            .catch((error) => {
                setIsOTPCodeValid(false);
                setIsSubmitAllowed(true);
            });
    }

    /*     const createDevice = (): void => {
            const request: DeviceRequest = { user: loginForm.username, deviceName: otpCreateDeviceForm.name };
            TOTPService.createDevice(request)
                .then((response) => response.data)
                .then((device: ICreateDevice) => {
                    setOtpActivateDeviceForm({ registrationUri: device.registrationUri || '', code: '' });
                    setDevice({ name: device.deviceName || '', authId: device.authId, token: device.token });
                    setPageMode(PageMode.OTP_ACTIVATE_DEVICE);
                    setIsValid(true);
                }).catch((error) => {
                    console.error(error);
                    setIsValid(false);
                });
        }; */

    const activateDevice = (): void => {
        const request: ActivateDeviceRequest = {
            user: loginForm.username,
            authId: device.authId,
            token: device.token,
            otpCode: otpCodeForm.code
        };

        TOTPService.activateDevice(request)
            .then((response) => response.data)
            .then((device: IBaseDevice) => {
                login();//Se reutiliza el mismo codigo otp google authenticator
                setIsValid(true);
            }).catch((error) => {
                console.error(error);
                setIsValid(false);
            });
    };



    const lostDeviceUnpairAll = (): void => {
        const request: UnpairOtpDevicesRequest = {
            username: loginForm.username,
            code: otpCodeForm.code
        };

        setIsOTPCodeValid(true);
        TOTPService.unpairOtpDevices(request)
            .then((response) => response.data)
            .then(() => {
                toast.success(t(`${T_PREFIX}otpCodeRequestLostDevice.successMessage`).toString());
                setPageMode(PageMode.LOGIN);
            }).catch((error) => {
                console.error(error);
                setIsOTPCodeValid(false);
            });
    }

    if (!loginLoaded) {
        loginLoaded = true;
        if (localStorage.getItem('credential') !== null) {
            loginWithFingerPrint()
        }
    }

    // #################### Render Methods ####################

    const renderLoginForm = (): ReactElement => {
        return (
            <>
                <Form model={[loginForm, setLoginForm]} onSubmit={requestOtp}>
                    <div className="panel panel-primary">
                        <div className="panel-body tabs-menu-body p-0">
                            <div className="tab-content">
                                <div className="tab-pane active" id="tab5">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <label className='label-input'>{t(`${T_PREFIX}username`)}</label>
                                            <EmailField attr='username' label={`${T_PREFIX}insertUsername`} required autoFocus></EmailField>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <label className='label-input'>{t(`${T_PREFIX}key`)}</label>
                                            <PasswordField attr='key' label="" required></PasswordField>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="finger-button" onClick={() => loginWithFingerPrint()} hidden={!fingerPrintEnabled}></div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <LabelMessage isLabelError={true} valid={!isLoginValid} message={t(`${(serverError==""||serverError==null) ? `${T_PREFIX}wrongUserOrPass` : t(`${T_PREFIX}${serverError}`) }`)}></LabelMessage>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="text-end">
                                                <p className="mb-5">
                                                    <Link to={'/forgot-password'} className="link link-underline">
                                                        <span>{t(`${T_PREFIX}passwordRecovery`)}</span>
                                                    </Link>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    {/* <div className="row">
                                        <div className="col-md-12">
                                            <div className="text-start my-2">
                                                <CheckBoxField id={"clientDocument"} label={"Recordar usuario"} onChange={(event) => console.log("")} checked={false}></CheckBoxField>
                                            </div>
                                        </div>
                                    </div> */}
                                    <div className="row">
                                        <div className="col-md-12">
                                            <SubmitButton className="mb-4 submitButton sendingButton">
                                                <span className='text-center'>{t(`${T_PREFIX}submitButton`)}</span>
                                            </SubmitButton>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12 d-flex justify-content-center">
                                            <p className='text-center w-90 register-text'> {t(`${T_PREFIX}areYouVisitor`)}
                                                <Link to={'/onboarding'} className="linkTo">
                                                    {t(`${T_PREFIX}register`)}
                                                </Link>
                                            </p>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12 d-flex justify-content-center mt-4">
                                            <Link to={'/help-outside'} className="linkTo">
                                                {t(`${T_PREFIX}needHelp`)}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>

            </>
        );
    }

    const renderFingerprintRegistration = (): ReactElement => {
        return (
            <>
                <Card>
                    <div className='text-center'>
                        <div className="finger-button" onClick={() => registerFingerPrint()} >
                            {/*<img src={fingerprint}  alt="logo"/>*/}
                        </div>
                        <h4 className="h4 mb-0">{t(`${T_PREFIX}fingerPrint.fingerauthentication`)}</h4>
                        <p className="card-text">{t(`${T_PREFIX}fingerPrint.fingerprintRegistrationQuestion`)}</p>
                    </div>
                </Card>
                <div className="text-center">
                    <Button onClick={handleCancel} label={t(`${T_PREFIX}otpCodeRequest.cancel`)} color='white' className='me-3' noBlock ></Button>
                </div>
            </>
        );
    }

    const renderPhoneNumberRequest = (): ReactElement => {
        return (
            <Form model={[phoneForm, setPhoneForm]} onSubmit={validatePhoneNumberAndRequestOtp}>
                <>
                    <p>{t(`${T_PREFIX}phoneNumberRequest.message`)}</p>
                    <TextField attr='number' label={`${T_PREFIX}phoneNumberRequest.phoneNumber`} required autoFocus ></TextField>
                    <LabelMessage valid={isPhoneNumberValid} message={t(`${T_PREFIX}phoneNumberRequest.wrongPhoneNumber`)} ></LabelMessage>
                    <div className="text-center">
                        <Button onClick={handleCancel} label={t(`${T_PREFIX}phoneNumberRequest.cancel`)} color='white' className='me-3' noBlock ></Button>
                        <SubmitButton label={t(`${T_PREFIX}phoneNumberRequest.accept`)} noBlock ></SubmitButton>
                    </div>
                </>
            </Form>
        );
    }

    const renderOtpCodeRequest = (onSubmit: (event: React.FormEvent<HTMLFormElement>) => void, tPrefix: string): ReactElement => {
        return (
            <Form model={[otpCodeForm, setOtpCodeForm]} onSubmit={onSubmit}>
                <>
                    <div className="w-100 mb-2 mt-3 d-flex align-items-center justify-content-start">
                        <Icon name="phone_android"/>
                        <span className='ms-2 fs-7'>{t(`${tPrefix}message`)}</span>
                    </div>
                    {UtilService.isMobilePlatform() ? <TextField className='pb-4' label="" attr='code' required autoFocus></TextField> : <AuthenticatorCodeField className='pb-4' label="" attr='code' required autoFocus></AuthenticatorCodeField>}
                    <LabelMessage isLabelError={true} valid={isOTPCodeValid} message={t(`${tPrefix}wrongCode`)} ></LabelMessage>
                    <div className="text-center">
                        <SubmitButton className="submitButton" disabled={otpCodeForm.code.length < 6 || !isSubmitAllowed} label={t(`${T_PREFIX}otpActivateDevice.singin`)} noBlock ></SubmitButton>
                    </div>
                    <div className="row">
                        <div className="col-md-12 d-flex justify-content-center mt-5">
                            <p className='text-center linkTo'>{t(`${T_PREFIX}needHelp`)}</p>
                        </div>
                        <div className="col-md-12 text-center">
                            <p className="mb-0">
                                <Link to={'/lost-phone'} className="link">
                                    <span>{t(`${T_PREFIX}lostMyPhone`)}</span>
                                </Link>
                            </p>
                        </div>
                    </div>

                </>
            </Form>
        );
    }

    const renderOtpCodeRequestAuthenticator = (): ReactElement => {
        return renderOtpCodeRequest(login, `${T_PREFIX}otpCodeRequest.`);
    }

    const renderOtpCodeRequestLostDevice = (): ReactElement => {
        return renderOtpCodeRequest(lostDeviceUnpairAll, `${T_PREFIX}otpCodeRequestLostDevice.`);
    }

    /* const renderOtpCreateDevice = (): ReactElement => {
        return (
            <Form model={[otpCreateDeviceForm, setOtpCreateDeviceForm]} onSubmit={createDevice}>
                <>
                    <TextField attr='name' label={`${T_PREFIX}otpCreateDevice.name`} required autoFocus ></TextField>
                    <div className="text-center">
                        <Button onClick={handleCancel} label={t(`${T_PREFIX}otpCreateDevice.cancel`)} color='white' className='me-3' noBlock ></Button>
                        <SubmitButton label={t(`${T_PREFIX}otpCreateDevice.accept`)} noBlock ></SubmitButton>
                    </div>
                    <div className="row mt-2">
                        <div className="col-md-12">
                            <div className="text-center">
                                <p className="mb-0">
                                    <Link to={'/lost-phone'} className="link">
                                        <span>{t(`${T_PREFIX}lostMyPhone`)}</span>
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </div> 
                </>
            </Form>
        );
    } */

    const RenderOtpActivateDevice = (): ReactElement => {

        const getSecret = (url: string): string => {
            const parts = url.split("secret=");
            return parts[1] ?? "";
        };

        const handleCopyIntoClipBoard = async (url: string) => {
            try {
                let value= getSecret(url);
                await navigator.clipboard.writeText(value);
                setCopiedQR(true);
                setTimeout(() => setCopiedQR(false), 5000);
            } catch (err) {
                console.error('Error al copiar la URL:', err);
            }
        };

        return (
            <>
                {
                    pageActivateStep === PageActivateSteps.CONFIG ?
                        <div className="row">
                            <div className="col-12">
                                <small className="text-start mb-0">{t(`${T_PREFIX}otpActivateDevice.2faText`)}</small>
                            </div>
                            <div className="col-md-12">
                                <CodeAuthenticatorSVG className='p-5' />
                            </div>
                            <div className="col-md-12 d-flex justify-content-center">
                                <SubmitButton className='sendingButton' label={"pages.login.otpActivateDevice.startConfig"} onClick={() => setPageActiveStep(PageActivateSteps.QR_SCAN)} noBlock ></SubmitButton>
                            </div>
                        </div>
                        : pageActivateStep === PageActivateSteps.QR_SCAN ?
                            <div className="row">
                                <div className="col-md-12 my-4 d-flex">
                                    <div className="wrap-qr">
                                    <QRCodeSVG value={otpActivateDeviceForm?.registrationUri} style={{width:"100%",height:"100%"}}/>
                                    <a type="button" className="copyToClipboard" onClick={()=> handleCopyIntoClipBoard(otpActivateDeviceForm?.registrationUri)}>
                                            <Icon name={copiedQR ? 'check' : 'content_copy'} className="text-base" />
                                            <span>{t(`${T_PREFIX}otpActivateDevice.${copiedQR ? 'copied' : 'copyCode'}`)}</span>
                                        </a>
                                    </div>
                                </div>
                                <div className="col-md-12 d-flex justify-content-center mt-2">
                                    <SubmitButton className='sendingButton' label={"common.action.nextStep"} onClick={() => setPageActiveStep(PageActivateSteps.FIRST_CODE)} noBlock ></SubmitButton>
                                </div>
                            </div>
                            : pageActivateStep === PageActivateSteps.FIRST_CODE ?
                                <div className="row">
                                    <div className="col-md-12 my-4 d-flex w-80">
                                        <Form model={[otpCodeForm, setOtpCodeForm]}>
                                            <>
                                                <div className="w-100 mb-2 d-flex align-items-center justify-content-start">
                                            <Icon name="phone_android"/>
                                                    <p className='ms-2 mb-0'>{t(`${T_PREFIX}otpActivateDevice.codeGoogleAuthenticator`)}</p>
                                                </div>
                                        {UtilService.isMobilePlatform() ? <TextField className='pb-4' label="" attr='code' required autoFocus></TextField> :  <AuthenticatorCodeField className='pb-4' label="" attr='code' required autoFocus></AuthenticatorCodeField>}
                                                <LabelMessage isLabelError={true} valid={isOTPCodeValid} message={t(`${T_PREFIX}otpActivateDevice.wrongCode`)} ></LabelMessage>
                                            </>
                                        </Form>
                                    </div>
                                    <div className="col-md-12 d-flex justify-content-center mt-2">
                                <SubmitButton disabled={otpCodeForm?.code?.length < 6} className='sendingButton' label={t(`${T_PREFIX}otpActivateDevice.finish`)} onClick={() => activateDevice() } noBlock ></SubmitButton>
                                    </div>
                                </div>
                                : ""
                }
                <div className="row">
                    <div className="col-md-12 d-flex justify-content-center mt-4">
                        <Link to={'/help-outside'} className="linkTo">
                            {t(`${T_PREFIX}needHelp`)}
                        </Link>
                    </div>
                </div>
            </>
        );
    }

    const renderOtpChoiceDevice = (): ReactElement => {
        return (
            <Form model={[otpChoiceDeviceForm, setOtpChoiceDeviceForm]} onSubmit={requestOtp}>
                <>
                    <SelectField
                        attr='device'
                        options={otpChoiceDeviceOptions}
                        label={`${T_PREFIX}otpChoiceDevice.devices`}
                        onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                            setDevice({ name: event.target.value });
                        }}
                        required
                        autoFocus
                    ></SelectField>
                    <div className="text-center">
                        <Button onClick={handleCancel} label={t(`${T_PREFIX}otpChoiceDevice.cancel`)} color='white' className='me-3' noBlock ></Button>
                        <SubmitButton label={t(`${T_PREFIX}otpChoiceDevice.accept`)} noBlock ></SubmitButton>
                    </div>
                </>
            </Form>
        );
    }

    const buildRenderObject = (
        title: string,
        renderFunction: () => ReactElement,
        renderAsideFunction?: () => ReactElement,
        initialize?: { state: [any, Dispatch<SetStateAction<any>>], function: <T>(state: [T, Dispatch<SetStateAction<T>>]) => void }
    ): RenderObject => {
        return { title, renderFunction, renderAsideFunction, initialize };
    }

    const buildTitle = (sufix: string, options?: any): string => {
        return t(`${T_PREFIX}${sufix}`, options);
    }

    function initState<T>(stateParam: [T, Dispatch<SetStateAction<T>>]): void {
        let [state, setState] = stateParam;

        let needsToBeInitialized: boolean = false;
        for (const key in state) {
            needsToBeInitialized = Boolean(state[key]);
            if (needsToBeInitialized) {
                break;
            }
        }
        if (needsToBeInitialized) {
            setState(ObjectHelper.cleanObject(state));
        }
    }

    function initStateAndDevice<T>(stateParam: [T, Dispatch<SetStateAction<T>>]): void {
        initState(stateParam);
        setDevice({ name: '' });
    }

    const renderObjectsMap: Map<PageMode, RenderObject> = new Map([
        [
            PageMode.LOGIN,
            buildRenderObject(
                buildTitle('title'),
                renderLoginForm,
                ()=> <AuthAsideDefault />,
                { state: [loginForm, setLoginForm], function: initState }
            )
        ],
        [
            PageMode.PHONE_NUMBER_REQUEST,
            buildRenderObject(
                buildTitle('phoneNumberRequest.title'),
                renderPhoneNumberRequest,
                undefined,
                { state: [phoneForm, setPhoneForm], function: initState }
            )
        ],
        [
            PageMode.FINGERPRINT_REGISTRATION,
            buildRenderObject('', renderFingerprintRegistration)
        ],
        [
            PageMode.OTP_CODE_REQUEST,
            buildRenderObject(
                buildTitle('otpCodeRequest.title'),
                renderOtpCodeRequestAuthenticator,
                ()=> <AuthAsideOtp />,
                { state: [otpCodeForm, setOtpCodeForm], function: initStateAndDevice }
            )
        ],
        [
            PageMode.OTP_CODE_REQUEST_LOST_DEVICE,
            buildRenderObject(
                buildTitle('otpCodeRequestLostDevice.title'),
                renderOtpCodeRequestLostDevice,
                undefined,
                { state: [otpCodeForm, setOtpCodeForm], function: initStateAndDevice }
            )
        ],
        /*         [
                    PageMode.OTP_CREATE_DEVICE,
                    buildRenderObject(
                        buildTitle('otpCreateDevice.title'),
                        renderOtpCreateDevice,
                        undefined,
                        { state: [otpCreateDeviceForm, setOtpCreateDeviceForm], function: initStateAndDevice }
                    )
                ], */
        [
            PageMode.OTP_ACTIVATE_DEVICE,
            buildRenderObject(
                buildTitle(`otpActivateDevice.${pageActivateStep}title`, { deviceName: device.name }),
                RenderOtpActivateDevice,
                ()=> pageActivateStep === PageActivateSteps.CONFIG ? <AuthDownload2FA /> : pageActivateStep === PageActivateSteps.QR_SCAN ? <QrScan /> : pageActivateStep === PageActivateSteps.FIRST_CODE ? <Code2FA /> : <></>,
                { state: [otpActivateDeviceForm, setOtpActivateDeviceForm], function: initStateAndDevice }
            )
        ],
        [
            PageMode.OTP_CHOICE_DEVICE,
            buildRenderObject(
                buildTitle('otpChoiceDevice.title'),
                renderOtpChoiceDevice,
                undefined,
                { state: [otpChoiceDeviceForm, setOtpChoiceDeviceForm], function: initStateAndDevice }
            )
        ]
    ]);

    const getRenderObjectByPageMode = (pageMode: PageMode): RenderObject => {
        return renderObjectsMap.get(pageMode) || { title: "", renderFunction: () => <></>, renderAsideFunction: () => <></> } as RenderObject;
    }

    const renderTitle = () => {
        return getRenderObjectByPageMode(pageMode).title;
    }
    const renderAsideByPageMode = () => {
        return (
            <>
                { getRenderObjectByPageMode(pageMode).renderAsideFunction?.() }
            </>
        )
    }
    const renderByPageMode = () => {
        return (
            <>
                { getRenderObjectByPageMode(pageMode).renderFunction() }
            </>
        )
    }

    return (
        <React.Fragment>
            <ToastContainer autoClose={3000} ></ToastContainer>
            <div className="login" >
                <div className="page">
                    <div className="container-login100" style={{ backgroundImage: `url(${bgImage})`}}>
                        <div className="wrap-login100 p-6">
                            <BrandLogo />
                            { showExpiredPassword ? (
                                <div className="row">
                                    <div className="col-md-12 d-flex justify-content-center">
                                        <ExpiredPasswordSVG className='py-1 px-3' />
                                    </div>
                                    <div className="col-md-12 my-2">
                                        <h6 className='fw-bold text-center'>{t(`${T_PREFIX}expiredPasswordModal.title`)}</h6>
                                        <small className='d-block text-center'>{t(`${T_PREFIX}needToResetPassword`)}</small>
                                    </div>
                                    <div className="col-md-12 mt-3">
                                        <SubmitButton onClick={() => { sessionStorage.setItem('expiredUsername', loginForm.username); window.location.href = '/reset-password?expired=true'; }} className='sendingButton' label={t(`${T_PREFIX}expiredPasswordModal.button`)}></SubmitButton>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {  (pageMode === PageMode.OTP_ACTIVATE_DEVICE ) &&
                                        <StepProgress pageMode={PageModeOnboarding.OTP_ACTIVATE_DEVICE} />
                                    }
                                    <h2 className="login100-form-title text-start mb-2 mt-1">
                                        {renderTitle()}
                                    </h2>
                                    <>
                                        {renderByPageMode()}
                                    </>
                                </>
                            )}
                        </div>
                        { !showExpiredPassword && renderAsideByPageMode() }
                    </div>
                    {/* <div className='divRegister'>
                        <p className='registerMerchant'> {t(`${T_PREFIX}notYetAtFiserv`)} <Link to={'/onboarding'}
                                                                                                className='linkTo'>{t(`${T_PREFIX}register`)}</Link>
                        </p>
                        <p className='registerMerchant'> {t(`${T_PREFIX}googleAuthenticatorInstr`)} <a href="/Autenticacion_instr.pdf"
                                            className='linkTo'download>{t(`${T_PREFIX}download`)} </a>
                        </p>
                        {renderDownloadMerchantApp()}
                    </div> */}
                </div>
                <Footer isValid={isValid}/>
            </div>
        </React.Fragment>
    )
}
export default withTranslation()(Login);
